import React, { useEffect, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import ChatStore from 'chat/stores/ChatStore';
import Chats from 'collections/Chats';
import { Target } from 'entities/Message';
import PotentialChatMember from 'entities/PotentialChatMember';
import {
  loadGroupChats,
  loadGroups,
  loadIndividuals,
  loadOrganizations,
  loadPotentialChatMembers,
  searchGroupChats,
  selectInitialTargets,
} from 'reducers/roleBasedMessagingSearch';
import { ComposerGroup, ComposerOrganization } from 'types/typescript/messagingTypes';

export type Props = {
  groups?: ComposerGroup[];
  organizations?: ComposerOrganization[];
  initialTargets?: Target[];
  groupsLoading?: boolean;
  orgsLoading?: boolean;
};

type ReduxDispatchProps = {
  selectInitialTargets: (targets: Target[]) => void;
  loadOrganizations: (organizations: ComposerOrganization[]) => void;
  loadGroups: (groups: ComposerGroup[]) => void;
  loadGroupChats: (chats: Chats) => void;
  loadIndividuals: (members: PotentialChatMember[]) => void;
  loadPotentialChatMembers: (query: string) => void;
  searchGroupChats: (query: string) => void;
};

/* Takes groups, organizations, and initialTargets and loads them into redux via useEffect.
 */
function ComposerDataLoader({
  // component props
  initialTargets,
  groups = [],
  organizations = [],
  groupsLoading,
  orgsLoading,
  // redux props
  selectInitialTargets,
  loadGroups,
  loadOrganizations,
  searchGroupChats,
  loadGroupChats,
  loadIndividuals,
  loadPotentialChatMembers,
}: Props & ReduxDispatchProps) {
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);

  // On first render, load groups and individuals.
  useEffect(() => {
    // Make sure we show available group chats.
    const groupChats = ChatStore.getGroupChats();
    if (groupChats.length === 0) {
      searchGroupChats('');
    } else {
      loadGroupChats(groupChats);
    }
    // Make sure we have some people to show.
    const individuals = ChatStore.getEligibleMemberModels();
    if (individuals.length === 0) {
      loadPotentialChatMembers('');
    } else {
      loadIndividuals(individuals);
    }
  }, []);

  useEffect(() => {
    if (groups.length > 0 && !groupsLoading && !groupsLoaded) {
      loadGroups(groups);
      setGroupsLoaded(true);
    }
  }, [groups, groupsLoaded]);

  useEffect(() => {
    if (organizations.length > 0 && !orgsLoading && !organizationsLoaded) {
      loadOrganizations(organizations);
      setOrganizationsLoaded(true);
    }
  }, [organizations, organizationsLoaded]);

  // Once we're done loading recipients, update selection with initialTargets.
  // We do this once instead of every update to avoid causing render loops. Loading initialTargets
  // makes the reducer rebuild the `targets` array in state, which could trigger components higher
  // up the tree (like FullPageComposerView) to rerender even if the targets are actually the same.
  useEffect(() => {
    const groupsLoadedOrNoGroups = groups.length > 0 && !groupsLoading ? groupsLoaded : true;
    const orgsLoadedOrNoOrgs =
      organizations.length > 0 && !orgsLoading ? organizationsLoaded : true;
    if (groupsLoadedOrNoGroups && orgsLoadedOrNoOrgs && initialTargets)
      selectInitialTargets(initialTargets);
  }, [groupsLoaded, organizationsLoaded]);

  return <React.Fragment />;
}

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => {
  return bindActionCreators(
    {
      selectInitialTargets,
      loadGroups,
      loadOrganizations,
      loadGroupChats,
      loadIndividuals,
      loadPotentialChatMembers,
      searchGroupChats,
    },
    dispatch
  );
};

export { ComposerDataLoader };
export default connect(null, mapDispatchToProps)(ComposerDataLoader);
